<template>
  <div
    ref="padEl"
    :class="['card-pad g-ld-shadow']"
  >
    <div class="px-3 py-4">
      <div class="flex items-center gap-2 h-24px mb-2">
        <div
          :class="[
            'flex items-center text-[var(--gray-400)]',
            {
              'cursor-pointer': canSwitchCardType,
            },
          ]"
          @click="onCardTypeSwitch"
        >
          <span>{{ cardNameMap[card.type] }}</span>

          <i
            v-if="canSwitchCardType"
            class="pi pi-chevron-down ml-1 text-14px"
          ></i>
        </div>

        <div class="ml-auto flex gap-2 items-center">
          <Icon
            v-if="props.showFullEdit"
            name="fullscreen"
            class="cursor-pointer fullscreen w-20px"
            :draggable="false"
            @click.stop="onFullscreenEdit()"
          />

          <DragHandle
            v-if="props.showDragHandle"
            class="h-24px"
          >
            <Icon
              name="note-order"
              class="cursor-pointer order-drag-handle w-24px"
              :draggable="false"
            />
          </DragHandle>

          <CardMenu
            v-if="shouldShowMenu"
            :can-edit="props.showFullEdit"
            :can-delete="props.canDelete"
            :can-create="props.canCreate"
            :can-copy="props.canCopy"
            @edit="onFullscreenEdit()"
            @create-after="emit('create-after')"
            @create-before="emit('create-before')"
            @copy="emit('copy')"
            @delete="emit('delete')"
          />
        </div>
      </div>

      <ClozeCardPad
        v-if="card.type === CardType.CLOZE"
        :cardDraft="props.cardDraft"
        :card="card"
        :editorKey="editorKey"
        :illustration-uploading="illustrationUploading"
        @prev="emit('prev')"
        @next="emit('next')"
        @focus="emit('focus', 0)"
        @create-after="emit('create-after')"
        @add-illustration="onAddIllustration"
        @remove-illustration="onRemoveIllustration"
        @update="onClozeCardUpdate"
      />

      <EnWordCardPad
        v-else-if="card.type === CardType.EN_WORD"
        :cardRes="props.cardDraft"
        :card="card"
        :illustration-uploading="illustrationUploading"
        @focus="emit('focus', 0)"
        @add-illustration="onAddIllustration"
        @remove-illustration="onRemoveIllustration"
        @update="onEnWordCardUpdate"
      />

      <MCQCardPad
        v-else-if="card.type === CardType.MCQ"
        :cardRes="props.cardDraft"
        :card="card"
        :illustration-uploading="illustrationUploading"
        @focus="emit('focus', 0)"
        @add-illustration="onAddIllustration"
        @remove-illustration="onRemoveIllustration"
        @update="onMCQCardUpdate"
      />

      <KidHanziCardPad
        v-else-if="card.type === CardType.KID_HANZI"
        :cardRes="props.cardDraft"
        :card="card"
        :illustration-uploading="illustrationUploading"
        @focus="emit('focus', 0)"
        @add-illustration="onAddIllustration"
        @remove-illustration="onRemoveIllustration"
        @update="onKidHanziCardUpdate"
      />
    </div>

    <slot name="footer"></slot>
  </div>
</template>
<script setup lang="ts">
import { updatePackage } from '@/api/package-source'
import CardEdit from '@/pc/pages/Package/CardEdit.vue'
import CardMenu from '@/components/package/CardMenu.vue'
import {
  CardType,
  type Card,
  type ClozeCard,
  type EnWordCard,
  type MCQCard,
} from '@/types/core'
import { ref, watch, computed } from 'vue'
import { pickFile, resizeAndCompressImage } from '@/utils/misc'
import { uploadImage } from '@/api/user'
import { DragHandle } from 'vue-slicksort'
import CardTypeSwitchForm from './CardTypeSwitchForm.vue'
import {
  newClozeCard,
  newMCQCard,
  newWordCard,
  type CardDraft,
} from '@/utils/card'
import ClozeCardPad from './ClozeCardPad.vue'
import EnWordCardPad from './EnWordCardPad.vue'
import MCQCardPad from './MCQCardPad.vue'
import { convertCardTypeToCardName } from '@/shared'
import KidHanziCardPad from './KidHanziCardPad.vue'
import type { KidHanziCard } from '@/types/model'

const cardNameMap = {
  [CardType.CLOZE]: _t('cardview.keypoint'),
  [CardType.EN_WORD]: _t('cardview.enword'),
  [CardType.MCQ]: _t('cardview.mcq'),
  [CardType.KID_HANZI]: _t('cardview.kid_hanzi'),
}

const props = withDefaults(
  defineProps<{
    canDelete: boolean
    packageId: number
    cardDraft: CardDraft
    cards: CardDraft[]
    showFullEdit?: boolean
    canSwitchCardType?: boolean
    showDragHandle?: boolean
    showMenu?: boolean
    canCreate?: boolean
    canCopy?: boolean
  }>(),
  {
    canSwitchCardType: false,
    showFullEdit: false,
    showDragHandle: true,
    showMenu: true,
    canCreate: true,
    canCopy: true,
  }
)

const emit = defineEmits<{
  'create-before': []
  'create-after': []
  copy: []
  delete: []
  focus: [offset: number]
  update: [Card]
  prev: []
  next: []
  'add-illustration': [assetId: string]
  'remove-illustration': []
  'change-card-type': [CardType]
}>()

const padEl = ref<HTMLDivElement>()
const editorKey = ref(0)

const card = computed(() => JSON.parse(props.cardDraft.content) as Card)

// 检查是否应该显示菜单 - 只有在至少有一个菜单项可用时才显示
const shouldShowMenu = computed(() => {
  if (!props.showMenu) return false

  // 检查是否至少有一个菜单项是可用的
  return (
    props.showFullEdit || // 编辑
    props.canCreate || // 新建
    props.canCopy || // 复制
    props.canDelete
  ) // 删除
})

function onClozeCardUpdate(card: ClozeCard) {
  emit('update', card)
}

function onEnWordCardUpdate(card: EnWordCard) {
  emit('update', card)
}

function onKidHanziCardUpdate(card: KidHanziCard) {
  emit('update', card)
}

function onMCQCardUpdate(card: MCQCard) {
  emit('update', card)
}

function onFullscreenEdit() {
  if (props.cardDraft.id == null) {
    _message.info(_t('package.create_tip5'))
    return
  }

  _openDialog(CardEdit, {
    title: _t('common.edit'),
    props: {
      card: props.cardDraft,
      cards: props.cards,
      onUpdate(newCard: CardDraft) {
        editorKey.value++
        emit('update', JSON.parse(newCard.content))
      },
    },
    rootClass: 'w-1000px',
  })
}
const IMG_FILES = '.jpg,.jpeg,.png,.svg,.bmp,.webp,.gif'
const illustrationUploading = ref(false)
async function onAddIllustration() {
  // NOTE: 这里的实现并不好，因为整体的架构原因
  // 移动端点击 icon 时，不应该打开 File Picker，应该提示用户去下载 App
  // 但是这段逻辑又是上层负责的，这里只能耦合一份
  if (_global.isMobileMode && !_global.isInsideApp) {
    // 告诉上层去触发弹窗提示用户
    emit('focus', 0)
    return
  }

  if (card.value.illustration) return

  let file = await pickFile(IMG_FILES)

  // 修复文件名重复问题：对所有文件都生成唯一文件名，避免任何可能的重名
  const timestamp = Date.now()
  const originalExt = file.name.split('.').pop() || 'jpg' // 获取原始文件扩展名
  const newFileName = `card_${timestamp}.${originalExt}`

  // 创建新的File对象，使用新的文件名
  file = new File([file], newFileName, {
    type: file.type,
    lastModified: file.lastModified,
  })

  try {
    illustrationUploading.value = true
    const compressedFile = await resizeAndCompressImage(file, 2000, 2000)
    const res = await uploadImage(compressedFile)

    if (res.code != 0) {
      _message.info(res.message)
    } else {
      emit('add-illustration', res.data.assetId)
    }
  } finally {
    illustrationUploading.value = false
  }
}

function onCardTypeSwitch() {
  if (!props.canSwitchCardType) return

  _openDialog(CardTypeSwitchForm, {
    title: '切换卡片类型',
    rootClass: _global.isPcMode ? 'w-420px' : 'g-dialog',
    props: {
      card: card.value,
      packageId: props.packageId,
      onTypeSwitch(newType: CardType) {
        updatePackage(props.packageId, {
          defaultCardType: convertCardTypeToCardName(newType),
        })

        emit('change-card-type', newType)
        switch (newType) {
          case CardType.CLOZE:
            emit('update', newClozeCard())
            break
          case CardType.EN_WORD:
            emit('update', newWordCard())
            break
          case CardType.MCQ:
            emit('update', newMCQCard())
            break
        }
      },
    },
  })
}

function onRemoveIllustration() {
  emit('remove-illustration')
}

// 当 cardRes 变化时，需要更新编辑器
watch(
  () => props.cardDraft,
  () => {
    editorKey.value++
  }
)
</script>
<style scoped>
.card-pad {
  border-radius: 8px;
  width: 100%;
  min-height: 44px;
  border: 1px solid var(--ld-border);
  transition: all 0.3s;
  background-color: white;
}

.card-pad * {
  transition: all 0.3s;
}

.card-pad .fullscreen,
.card-pad .order-drag-handle {
  /* 当拖拽进行排序时，会选中输入框内的文本，所以这里需要禁止选中 */
  user-select: none;
  display: none;
}

.card-pad:hover .fullscreen,
.card-pad:hover .order-drag-handle {
  display: inline-block;
}

.illustration :deep(.p-image-preview-indicator) {
  opacity: 0;
}
</style>
